import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useCreateCase, useUpdateCase, useMarkets } from '@/hooks/usePartnerships';
import type { Case } from '../../../../shared/types/partnerships';
import { toLines, fromLines } from '../../../../shared/text-lines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  caseItem?: Case;
}

const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;

interface FormData {
  title: string; brand: string; industry: string; market: string;
  summary: string; objective: string; challenge: string; solution: string;
  results: string; highlights: string; productsUsed: string;
  packagesUsed: string; mediaSpend: string; kpis: string;
  driveLinks: string; status: string;
}


export function CaseFormDialog({ open, onOpenChange, partnerId, caseItem }: Props) {
  const { t } = useTranslation();
  const isEditing = !!caseItem;
  const create = useCreateCase();
  const update = useUpdateCase();
  const { data: markets = [] } = useMarkets();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');
  const marketValue = watch('market');

  useEffect(() => {
    if (open) {
      reset({
        title: caseItem?.title || '', brand: caseItem?.brand || '',
        industry: caseItem?.industry || '', market: caseItem?.market || '',
        summary: caseItem?.summary || '',
        objective: caseItem?.objective || '', challenge: caseItem?.challenge || '',
        solution: caseItem?.solution || '', results: toLines(caseItem?.results),
        highlights: toLines(caseItem?.highlights),
        productsUsed: toLines(caseItem?.productsUsed),
        packagesUsed: toLines(caseItem?.packagesUsed),
        mediaSpend: caseItem?.mediaSpend || '',
        kpis: toLines(caseItem?.kpis),
        driveLinks: toLines(caseItem?.driveLinks),
        status: caseItem?.status || 'PUBLISHED',
      });
    }
  }, [open, caseItem, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      title: data.title, brand: data.brand, industry: data.industry,
      market: data.market, summary: data.summary || undefined,
      objective: data.objective,
      challenge: data.challenge || undefined,
      solution: data.solution, results: fromLines(data.results),
      highlights: fromLines(data.highlights),
      productsUsed: fromLines(data.productsUsed),
      packagesUsed: fromLines(data.packagesUsed),
      mediaSpend: data.mediaSpend || undefined,
      kpis: fromLines(data.kpis),
      driveLinks: fromLines(data.driveLinks),
      status: data.status as Case['status'],
    };

    if (isEditing) {
      await update.mutateAsync({ caseId: caseItem!.caseId, ...payload, expectedUpdatedAt: caseItem!.updatedAt });
    } else {
      await create.mutateAsync({ partnerId, ...payload });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;
  const activeMarkets = markets.filter(m => m.active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.case_form.title_edit') : t('partnerships.case_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.title_label')}</Label>
            <Input {...register('title', { required: true })} placeholder={t('partnerships.case_form.title_placeholder')} />
            {errors.title && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.brand_label')}</Label>
              <Input {...register('brand', { required: true })} />
              {errors.brand && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.industry_label')}</Label>
              <Input {...register('industry')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.market_label')}</Label>
              {activeMarkets.length > 0 ? (
                <Select value={marketValue} onValueChange={v => setValue('market', v)}>
                  <SelectTrigger><SelectValue placeholder={t('partnerships.case_form.select_placeholder')} /></SelectTrigger>
                  <SelectContent>
                    {activeMarkets.map(m => <SelectItem key={m.marketId} value={m.name}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input {...register('market')} placeholder="Argentina" />
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.executive_summary_label')}</Label>
            <Textarea {...register('summary')} rows={2} placeholder={t('partnerships.case_form.executive_summary_placeholder')} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.objective_label')}</Label>
            <Textarea {...register('objective')} rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.challenge_label')}</Label>
            <Textarea {...register('challenge')} rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.solution_label')}</Label>
            <Textarea {...register('solution')} rows={3} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.results_label')}</Label>
            <Textarea {...register('results')} rows={3} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.highlights_label')}</Label>
            <Textarea {...register('highlights')} rows={3} placeholder={t('partnerships.case_form.40_roas_10_20_cpm_10')} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.products_used_label')}</Label>
              <Textarea {...register('productsUsed')} rows={3} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.packages_used_label')}</Label>
              <Textarea {...register('packagesUsed')} rows={3} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.media_spend_label')}</Label>
              <Input {...register('mediaSpend')} type="number" min={0} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.case_form.kpis_label')}</Label>
              <Textarea {...register('kpis')} rows={3} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.case_form.drive_links_label')}</Label>
            <Textarea {...register('driveLinks')} rows={2} placeholder="https://drive.google.com/..." />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.status_label')}</Label>
            <Select value={statusValue} onValueChange={v => setValue('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.case_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
